import { useCallback, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { ProtectedRoute } from '../auth/ProtectedRoute'
import { OnboardingGuard } from '../auth/OnboardingGuard'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { useTheme } from '../../contexts/ThemeContext'
import { ImpersonationBanner } from '../ui/ImpersonationBanner'
import { useImpersonation } from '../../contexts/ImpersonationContext'
import { GlobalFiltersProvider } from '../../contexts/GlobalFiltersContext'
import { BUProvider } from '../../contexts/BUContext'
import { GlobalFiltersBar } from '../ui/GlobalFiltersBar'

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [filterBarOpen, setFilterBarOpen] = useState(false)
  const { isDark } = useTheme()
  const { impersonation } = useImpersonation()
  const openSidebar  = useCallback(() => setSidebarOpen(true), [])
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const toggleFilterBar = useCallback(() => setFilterBarOpen(v => !v), [])
  const closeFilterBar = useCallback(() => setFilterBarOpen(false), [])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
    return () => document.body.classList.remove('overflow-hidden')
  }, [sidebarOpen])

  return (
    <ProtectedRoute>
      <OnboardingGuard>
      <GlobalFiltersProvider>
      <BUProvider>
      <ImpersonationBanner />
      <div className="flex h-screen overflow-hidden bg-[--km-bg-deep]" style={impersonation ? { paddingTop: 32 } : undefined}>

        {/* Decorative background orbs — solo in dark mode */}
        {isDark && (
          <div className="pointer-events-none fixed inset-0 overflow-hidden z-0" aria-hidden="true">
            <div className="absolute -top-32 -right-16 w-[700px] h-[700px] rounded-full opacity-70"
              style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 65%)', animation: 'orbPulse 12s ease-in-out infinite' }} />
            <div className="absolute top-1/2 -left-48 w-[550px] h-[550px] rounded-full opacity-60"
              style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.07) 0%, transparent 65%)', animation: 'orbPulse 16s ease-in-out infinite 4s' }} />
            <div className="absolute -bottom-16 right-1/3 w-[450px] h-[450px] rounded-full opacity-50"
              style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 65%)', animation: 'orbPulse 14s ease-in-out infinite 8s' }} />
          </div>
        )}

        <Sidebar open={sidebarOpen} onClose={closeSidebar} />

        <div className="relative z-10 flex flex-1 flex-col min-w-0 overflow-hidden transition-colors duration-300 bg-[--km-bg-deep]">
          <Topbar onMenuClick={openSidebar} onFilterClick={toggleFilterBar} filterActive={filterBarOpen} />

          {/* Barra filtri globali — visibile quando aperta o con filtri attivi */}
          <GlobalFiltersBar isOpen={filterBarOpen} onClose={closeFilterBar} />

          <main className="flex-1 overflow-y-auto px-4 py-6 md:px-6 lg:px-8 animate-fadeInUp">
            <div className="max-w-[1400px] mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      </BUProvider>
      </GlobalFiltersProvider>
      </OnboardingGuard>
    </ProtectedRoute>
  )
}
